import { ChevronsUpDown } from "lucide-react";
import { Skeleton } from "@runcheetah/ui";
import { InfoTooltip } from "@/components/common";
import { JsonBlock } from "@/components/common/json-block";
import { MaterializedPromptPanel } from "@/components/llm/materialized-prompt-panel";
import { MoneyValue } from "@/components/common/money-value";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

import { EMPTY_BRANDKIT } from "../constants";
import type {
  SceneBlock,
  ScenePlan,
  SocialPostGenerationTask,
  SocialPostGenerationTaskDetail,
} from "../types";
import {
  CarouselCopyPanel,
  CarouselSlideRail,
  CarouselSlideSummary,
  getCarouselReviewState,
  type CarouselReviewOutputLike,
} from "./carousel-slide-review";
import { ContinuityReferenceGrid } from "./continuity-reference-grid";
import { CopyTracePanel } from "./copy-trace-panel";
import { ImageCandidateGroups } from "./image-candidate-groups";
import { LinkedAssetGrid } from "./linked-asset-grid";
import { LlmCostPanel } from "./llm-cost-panel";
import { SceneBlockInspector } from "./scene-block-inspector";
import { SceneLayerPanel } from "./scene-layer-panel";
import { SceneTemplatePreview } from "./scene-template-preview";

export type PlannerView = "creative" | "technical";

type PlannerTrace = Record<string, unknown>;

const getTraceString = (trace: PlannerTrace | null | undefined, key: string): string | null => {
  const value = trace?.[key];
  return typeof value === "string" && value.trim().length > 0 ? value : null;
};

const getTraceNumber = (trace: PlannerTrace | null | undefined, key: string): number | null => {
  const value = trace?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const getTraceBoolean = (trace: PlannerTrace | null | undefined, key: string): boolean | null => {
  const value = trace?.[key];
  return typeof value === "boolean" ? value : null;
};

const getTraceRecord = (
  trace: PlannerTrace | null | undefined,
  key: string,
): PlannerTrace | null => {
  const value = trace?.[key];
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as PlannerTrace)
    : null;
};

const getTraceStringArray = (trace: PlannerTrace | null | undefined, key: string): string[] => {
  const value = trace?.[key];
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === "string" && item.length > 0);
};

const getTraceArrayLength = (trace: PlannerTrace | null | undefined, key: string): number => {
  const value = trace?.[key];
  return Array.isArray(value) ? value.length : 0;
};

function TraceDiagnosticValue({
  label,
  value,
}: {
  label: string;
  value: string | string[] | null;
}) {
  const values = Array.isArray(value) ? value : value ? [value] : [];

  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 flex min-h-6 flex-wrap items-center gap-1.5">
        {values.length > 0 ? (
          values.map((item) => (
            <span key={item} className="rounded-md bg-background px-2 py-1 font-mono text-xs">
              {item}
            </span>
          ))
        ) : (
          <span className="font-mono text-xs text-muted-foreground">--</span>
        )}
      </div>
    </div>
  );
}

export function PlannerTraceSummary({ trace }: { trace: PlannerTrace | null | undefined }) {
  const plannerMode = getTraceString(trace, "plannerMode") ?? "unknown";
  const selectedFormatName = getTraceString(trace, "selectedFormatName");
  const selectedFormatKey = getTraceString(trace, "selectedFormatKey");
  const slideCount = getTraceNumber(trace, "slideCount");
  const rewriteCount = getTraceNumber(trace, "rewriteCount");
  const deterministicIssues = getTraceArrayLength(trace, "deterministicIssues");
  const reviewerIssues = getTraceArrayLength(trace, "reviewerIssues");
  const unresolvedDeterministicIssues = getTraceArrayLength(trace, "unresolvedDeterministicIssues");
  const unresolvedReviewerIssues = getTraceArrayLength(trace, "unresolvedReviewerIssues");
  const usedValidationTools = getTraceBoolean(trace, "usedValidationTools");
  const acceptedCandidateCount = getTraceNumber(trace, "acceptedCandidateCount");
  const repairedDeterministicIssues = Math.max(
    0,
    deterministicIssues - unresolvedDeterministicIssues,
  );
  const repairedReviewerIssues = Math.max(0, reviewerIssues - unresolvedReviewerIssues);
  const stageCalls = Array.isArray(trace?.stageCalls) ? trace.stageCalls : [];
  const selectedRecipeLabel = getTraceString(trace, "selectedRecipeLabel");
  const selectedRecipeId = getTraceString(trace, "selectedRecipeId");
  const selectedTemplateName = getTraceString(trace, "selectedTemplateName");
  const selectedTemplateId = getTraceString(trace, "selectedTemplateId");
  const recipeRationale = getTraceString(trace, "recipeSelectionRationale");
  const recipeConfidence = getTraceNumber(trace, "recipeSelectionConfidence");
  const templateRationale = getTraceString(trace, "templateSelectionRationale");
  const templateConfidence = getTraceNumber(trace, "templateSelectionConfidence");
  const usedFreeformFallback = getTraceBoolean(trace, "usedFreeformFallback") ?? false;
  const fallbackReason = getTraceString(trace, "freeformFallbackReason");
  const recipeContentSummary = getTraceRecord(trace, "recipeContentSummary");
  const visualTone = getTraceString(recipeContentSummary, "visualTone");
  const styleOverrideFields = getTraceStringArray(recipeContentSummary, "styleOverrideFields");
  const recipeSpecificFields = getTraceStringArray(recipeContentSummary, "recipeSpecificFields");
  const contractControls = getTraceStringArray(recipeContentSummary, "contractControls");
  const templateContentSummary = getTraceRecord(trace, "templateContentSummary");
  const templateTextSlotIds = getTraceStringArray(templateContentSummary, "textSlotIds");
  const templateImageSlotIds = getTraceStringArray(templateContentSummary, "imageSlotIds");
  const templateControlIds = getTraceStringArray(templateContentSummary, "controlIds");
  const templatePatchFields = getTraceStringArray(templateContentSummary, "patchFields");
  const templateSafeZoneHintCount = getTraceNumber(templateContentSummary, "safeZoneHintCount");
  const templateValidationIssues = getTraceStringArray(trace, "templateValidationIssues");
  const hasTemplateDiagnostics = Boolean(
    selectedTemplateName || selectedTemplateId || templateContentSummary,
  );
  const hasRecipeDiagnostics = Boolean(
    selectedRecipeLabel || selectedRecipeId || recipeContentSummary,
  );
  const selectedLayoutName = hasTemplateDiagnostics
    ? (selectedTemplateName ?? selectedTemplateId ?? "No template recorded")
    : hasRecipeDiagnostics
      ? (selectedRecipeLabel ?? selectedRecipeId ?? "Recipe-derived plan")
      : "Creative freeform plan";
  const selectedLayoutId = hasTemplateDiagnostics
    ? selectedTemplateId
    : hasRecipeDiagnostics
      ? selectedRecipeId
      : null;
  const selectedLayoutLabel = hasTemplateDiagnostics
    ? "Selected Template"
    : hasRecipeDiagnostics
      ? "Selected Recipe"
      : "Plan Type";
  const selectionRationale = hasTemplateDiagnostics
    ? templateRationale
    : hasRecipeDiagnostics
      ? recipeRationale
      : null;
  const selectionConfidence = hasTemplateDiagnostics
    ? templateConfidence
    : hasRecipeDiagnostics
      ? recipeConfidence
      : null;

  if (!trace) {
    return null;
  }

  if (plannerMode === "carousel_story") {
    return (
      <div className="space-y-3 rounded-3xl border bg-background p-4 shadow-xs">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold">Carousel Decision</h3>
          <InfoTooltip
            label="Explain carousel decision"
            content="Summarizes the planner's selected carousel format, slide count, unresolved issues, repair history, and generation stages for this attempt."
          />
          <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
            {plannerMode}
          </span>
          {rewriteCount && rewriteCount > 0 ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
              {rewriteCount} rewrite{rewriteCount === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>

        <div className="grid gap-3 text-sm md:grid-cols-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Selected Format
            </div>
            <div className="mt-1 font-medium text-foreground">
              {selectedFormatName ?? selectedFormatKey ?? "No format recorded"}
            </div>
            {selectedFormatKey ? (
              <div className="mt-1 font-mono text-xs text-muted-foreground">
                {selectedFormatKey}
              </div>
            ) : null}
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Slides
            </div>
            <div className="mt-1 font-mono text-lg font-semibold text-foreground">
              {slideCount ?? "--"}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Active Issues
            </div>
            <div className="mt-1 font-mono text-xs text-muted-foreground">
              {unresolvedDeterministicIssues} deterministic · {unresolvedReviewerIssues} semantic
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Repair History
            </div>
            <div className="mt-1 font-mono text-xs text-muted-foreground">
              {repairedDeterministicIssues} deterministic · {repairedReviewerIssues} semantic
            </div>
          </div>
        </div>

        <div className="grid gap-3 rounded-2xl bg-muted/30 p-3 text-sm md:grid-cols-4">
          {stageCalls.slice(0, 4).map((stage, index) => {
            const record =
              stage && typeof stage === "object" && !Array.isArray(stage)
                ? (stage as PlannerTrace)
                : null;
            const stageName = getTraceString(record, "stage") ?? `stage_${index + 1}`;
            const status = getTraceString(record, "status") ?? "--";
            return (
              <TraceDiagnosticValue
                key={`${stageName}-${index}`}
                label={stageName}
                value={status}
              />
            );
          })}
          {stageCalls.length === 0 ? (
            <TraceDiagnosticValue label="Stages" value="No stage calls recorded" />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-3xl border bg-background p-4 shadow-xs">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold">Layout Decision</h3>
        <InfoTooltip
          label="Explain layout decision"
          content={
            hasTemplateDiagnostics
              ? "Shows which layout template was selected, why it matched the brief, and which template slots were patched."
              : hasRecipeDiagnostics
                ? "Shows which planner recipe was selected, why it matched the brief, and whether freeform fallback was needed."
                : "Shows that this result came from creative freeform planning rather than a selected recipe or template."
          }
        />
        <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
          {plannerMode}
        </span>
        {usedFreeformFallback ? (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
            freeform fallback
          </span>
        ) : null}
      </div>

      <div className="grid gap-3 text-sm md:grid-cols-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {selectedLayoutLabel}
          </div>
          <div className="mt-1 font-medium text-foreground">{selectedLayoutName}</div>
          {selectedLayoutId ? (
            <div className="mt-1 font-mono text-xs text-muted-foreground">{selectedLayoutId}</div>
          ) : null}
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Selection Confidence
            <InfoTooltip
              label="Explain selection confidence"
              content="Model-reported confidence for the selected recipe or template when one was selected. It is directional, not a pass/fail score."
              className="size-4"
            />
          </div>
          <div className="mt-1 font-mono text-lg font-semibold text-foreground">
            {selectionConfidence === null ? "--" : `${Math.round(selectionConfidence * 100)}%`}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Builder
          </div>
          <div className="mt-1 font-mono text-xs text-muted-foreground">
            {getTraceString(trace, "deterministicBuilderVersion") ?? "--"}
          </div>
        </div>
      </div>

      <div className="grid gap-3 rounded-2xl bg-muted/30 p-3 text-sm md:grid-cols-4">
        {hasTemplateDiagnostics ? (
          <>
            <TraceDiagnosticValue label="Text Slots" value={templateTextSlotIds} />
            <TraceDiagnosticValue label="Image Slots" value={templateImageSlotIds} />
            <TraceDiagnosticValue
              label="Patch Fields"
              value={templatePatchFields.length > 0 ? templatePatchFields : templateControlIds}
            />
            <TraceDiagnosticValue
              label="Safe Zones"
              value={templateSafeZoneHintCount === null ? null : String(templateSafeZoneHintCount)}
            />
          </>
        ) : hasRecipeDiagnostics ? (
          <>
            <TraceDiagnosticValue label="Visual Tone" value={visualTone} />
            <TraceDiagnosticValue label="Color Overrides" value={styleOverrideFields} />
            <TraceDiagnosticValue label="Recipe Fields" value={recipeSpecificFields} />
            <TraceDiagnosticValue label="Recipe Controls" value={contractControls} />
          </>
        ) : (
          <>
            <TraceDiagnosticValue label="Creative Mode" value="Freeform" />
            <TraceDiagnosticValue label="Planner Mode" value={plannerMode} />
            <TraceDiagnosticValue
              label="Accepted Candidates"
              value={acceptedCandidateCount === null ? null : String(acceptedCandidateCount)}
            />
            <TraceDiagnosticValue
              label="Validation Tools"
              value={
                usedValidationTools === null ? null : usedValidationTools ? "Used" : "Not used"
              }
            />
          </>
        )}
      </div>

      {selectionRationale ? (
        <p className="text-sm leading-6 text-muted-foreground">{selectionRationale}</p>
      ) : null}
      {hasTemplateDiagnostics && templateValidationIssues.length > 0 ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <div className="font-semibold">Template Diagnostics</div>
          <ul className="mt-2 space-y-1">
            {templateValidationIssues.map((issue, index) => (
              <li key={`${issue}-${index}`}>{issue}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {fallbackReason ? (
        <p className="rounded-2xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Fallback reason: {fallbackReason}
        </p>
      ) : null}
    </div>
  );
}

export function PlannerDecisionDisclosure({ trace }: { trace: PlannerTrace | null | undefined }) {
  if (!trace) {
    return null;
  }

  return (
    <Collapsible className="w-full max-w-2xl rounded-2xl border bg-background/85 shadow-xs">
      <div className="flex items-center gap-2 px-4 py-3 transition-colors hover:bg-muted/30">
        <CollapsibleTrigger className="flex flex-1 items-center justify-between gap-3 text-left text-sm font-semibold">
          <span>Why this result?</span>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        </CollapsibleTrigger>
        <InfoTooltip
          label="Explain result decision"
          content="Open this when you need the planner's recipe, template, confidence, and fallback details. It is hidden by default to keep creative review focused on the final post."
        />
      </div>
      <CollapsibleContent className="border-t p-3">
        <PlannerTraceSummary trace={trace} />
      </CollapsibleContent>
    </Collapsible>
  );
}

const formatHashtag = (hashtag: string): string => `#${hashtag.replace(/^#/, "")}`;

function PublishCopyPanel({
  latestCopy,
}: {
  latestCopy: SocialPostGenerationTaskDetail["latestCopy"] | null | undefined;
}) {
  if (!latestCopy) {
    return null;
  }

  const metadata = latestCopy.result?.metadata;

  return (
    <div className="space-y-3 rounded-3xl border bg-background p-4 shadow-xs">
      <div>
        <h3 className="text-sm font-semibold">Publish Copy</h3>
        <p className="text-xs text-muted-foreground">
          Latest completed caption, hashtags, and alt text for this scheduled post.
        </p>
      </div>

      {latestCopy.lastError ? (
        <div className="rounded-2xl border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {latestCopy.lastError}
        </div>
      ) : null}

      {metadata ? (
        <div className="space-y-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Caption
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-foreground/80">
              {metadata.caption}
            </p>
          </div>

          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Hashtags
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {metadata.hashtags.map((hashtag) => (
                <span
                  key={hashtag}
                  className="rounded-full bg-muted px-2.5 py-1 font-mono text-xs text-foreground/72"
                >
                  {formatHashtag(hashtag)}
                </span>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Alt Text
            </div>
            <p className="mt-1 text-sm leading-6 text-foreground/80">{metadata.altText}</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Latest publish-copy result could not be parsed for this trace version.
        </p>
      )}

      <div className="break-all font-mono text-[10px] text-muted-foreground">
        Copy task {latestCopy.id}
      </div>
    </div>
  );
}

export function PlannerOutput({
  view,
  selectedTask,
  selectedTaskDetails,
  selectedTaskDetailsQuery,
  selectedPlan,
  carousel,
  selectedCarouselSlideId,
  selectedSceneBlock,
  selectedSceneBlockId,
  onSelectCarouselSlideId,
  onSelectSceneBlock,
}: {
  view: PlannerView;
  selectedTask: SocialPostGenerationTask | null;
  selectedTaskDetails: SocialPostGenerationTaskDetail | null;
  selectedTaskDetailsQuery: {
    isPending: boolean;
    error: { message: string } | null;
  };
  selectedPlan: ScenePlan | null;
  carousel?: CarouselReviewOutputLike | null;
  selectedCarouselSlideId?: string | null;
  selectedSceneBlock: SceneBlock | null;
  selectedSceneBlockId: string | null;
  onSelectCarouselSlideId?: (id: string) => void;
  onSelectSceneBlock: (id: string | null) => void;
}) {
  if (!selectedTask) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
        Select a sandbox attempt to inspect its output.
      </div>
    );
  }

  const carouselState = getCarouselReviewState(carousel, selectedCarouselSlideId ?? null);
  const previewLinkedAssets = carouselState.isCarousel
    ? carouselState.selectedAssets
    : (selectedTaskDetails?.linkedAssets ?? []);
  const inspectorLinkedAssets = carouselState.isCarousel
    ? (selectedTaskDetails?.linkedAssets ?? []).filter(
        (asset) => asset.slideId === carouselState.selectedSlideId,
      )
    : (selectedTaskDetails?.linkedAssets ?? []);
  const latestCopy = selectedTaskDetails?.latestCopy;
  const selectedTaskStatus = selectedTaskDetails?.status ?? selectedTask.status;
  const planLoadingCopy = selectedTaskDetailsQuery.isPending
    ? {
        title: "Loading social plan",
        description: "Fetching the latest generation output for this attempt.",
      }
    : selectedTaskStatus === "queued"
      ? {
          title: "Social plan queued",
          description: "Waiting for the worker to start this generation attempt.",
        }
      : selectedTaskStatus === "processing"
        ? {
            title: "Generating social plan",
            description: "The preview will appear here when the worker finishes this attempt.",
          }
        : null;

  if (view === "technical") {
    return (
      <div className="h-full w-full overflow-y-auto bg-muted/20">
        <div className="mx-auto w-full max-w-7xl space-y-6 p-6">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border bg-card p-4 shadow-xs md:col-span-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Task ID
              </div>
              <div className="mt-2 break-all font-mono text-sm text-foreground">
                {selectedTask.id}
              </div>
            </div>
            <div className="rounded-2xl border bg-card p-4 shadow-xs">
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Recorded Cost
                <InfoTooltip
                  label="Explain recorded cost"
                  content="Estimated LLM usage cost recorded for this generation attempt. Unpriced usage events are shown separately when pricing metadata is missing."
                  className="size-4"
                />
              </div>
              <div className="mt-2 font-mono text-2xl font-semibold tabular-nums">
                {selectedTaskDetails?.llmCosts ? (
                  <MoneyValue amount={selectedTaskDetails.llmCosts.totalCostUsd} />
                ) : (
                  "--"
                )}
              </div>
              {selectedTaskDetails?.llmCosts.unpricedEventCount ? (
                <div className="mt-1 text-xs text-amber-700">
                  {selectedTaskDetails.llmCosts.unpricedEventCount} unpriced usage event
                  {selectedTaskDetails.llmCosts.unpricedEventCount === 1 ? "" : "s"}
                </div>
              ) : null}
            </div>
          </div>

          {selectedTaskDetailsQuery.isPending ? (
            <Skeleton className="h-56 w-full" />
          ) : selectedTaskDetailsQuery.error ? (
            <div className="w-full overflow-hidden rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {selectedTaskDetailsQuery.error.message}
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {selectedTaskDetails?.llmCosts ? (
                <div className="space-y-3 rounded-3xl border bg-background p-4 shadow-xs">
                  <div>
                    <h3 className="text-sm font-semibold">LLM Costs Breakdown</h3>
                    <p className="text-xs text-muted-foreground">
                      Component-level usage for the selected attempt.
                    </p>
                  </div>
                  <LlmCostPanel costs={selectedTaskDetails.llmCosts} />
                </div>
              ) : null}

              <PlannerTraceSummary trace={selectedTaskDetails?.plannerTrace} />

              <MaterializedPromptPanel prompts={selectedTaskDetails?.materializedPrompts} />

              <PublishCopyPanel latestCopy={latestCopy} />

              <CopyTracePanel
                trace={latestCopy?.result?.copyTrace}
                title="Publish Copy Decisions"
              />

              <CopyTracePanel
                trace={selectedTaskDetails?.result?.copyTrace}
                title="Embedded Copy Decisions"
              />

              {carousel ? (
                <div className="w-full space-y-3 rounded-3xl border bg-background p-4 shadow-xs">
                  <CarouselSlideRail
                    carousel={carousel}
                    selectedSlideId={carouselState.selectedSlideId}
                    onSelectSlideId={onSelectCarouselSlideId ?? (() => {})}
                  />
                  <CarouselCopyPanel carousel={carousel} />
                </div>
              ) : null}

              {selectedTaskDetails ? (
                <div className="w-full space-y-3 rounded-3xl border bg-background p-4 shadow-xs">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h4 className="text-sm font-semibold">Carousel Continuity References</h4>
                      <p className="text-xs text-muted-foreground">
                        Preliminary reference images generated to keep carousel casts, settings, and
                        objects consistent across slides.
                      </p>
                    </div>
                    <span className="rounded-full bg-muted px-2.5 py-1 font-mono text-xs text-muted-foreground">
                      {selectedTaskDetails.continuityReferences.length} reference
                      {selectedTaskDetails.continuityReferences.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <ContinuityReferenceGrid references={selectedTaskDetails.continuityReferences} />
                </div>
              ) : null}

              {selectedTaskDetails ? (
                <div className="w-full space-y-3 rounded-3xl border bg-background p-4 shadow-xs">
                  <div>
                    <h4 className="text-sm font-semibold">Realized Scene Images</h4>
                    <p className="text-xs text-muted-foreground">
                      Generated image blocks and the prompts/references used to create them.
                    </p>
                  </div>
                  <LinkedAssetGrid assets={selectedTaskDetails.linkedAssets} />
                </div>
              ) : null}

              {selectedTaskDetails ? (
                <ImageCandidateGroups
                  groups={selectedTaskDetails.imageCandidateGroups}
                  qaDiagnostics={selectedTaskDetails.imageQaDiagnostics}
                />
              ) : null}

              <div className="w-full space-y-2 rounded-3xl border bg-background p-4 shadow-xs">
                <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Request JSON
                </h4>
                <JsonBlock value={selectedTaskDetails?.request ?? selectedTask.request} />
              </div>

              <div className="w-full space-y-2 rounded-3xl border bg-background p-4 shadow-xs">
                <h4 className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  Result JSON
                </h4>
                <JsonBlock value={selectedTaskDetails?.result ?? selectedTask.result} />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Creative view
  return (
    <div className="flex h-full w-full bg-muted/10">
      {/* Left Sidebar — Layers */}
      {selectedPlan ? (
        <div className="flex w-72 shrink-0 flex-col border-r bg-background">
          <div className="flex-1 space-y-5 overflow-y-auto p-4">
            <CarouselSlideRail
              carousel={carousel}
              selectedSlideId={carouselState.selectedSlideId}
              onSelectSlideId={onSelectCarouselSlideId ?? (() => {})}
            />
            <SceneLayerPanel
              plan={selectedPlan}
              linkedAssets={selectedTaskDetails?.linkedAssets ?? []}
              brandkit={selectedTaskDetails?.brandkit ?? EMPTY_BRANDKIT}
              selectedBlockId={selectedSceneBlockId}
              onSelectBlock={(id) => onSelectSceneBlock(id)}
            />
          </div>
        </div>
      ) : null}

      {/* Middle Canvas */}
      <div className="relative flex min-w-0 flex-1 flex-col items-center justify-center overflow-auto bg-[radial-gradient(circle_at_top,hsl(var(--background))_0,hsl(var(--muted))_70%)]">
        {selectedPlan ? (
          <div className="flex w-full flex-col items-center p-6 xl:p-10">
            <CarouselSlideSummary
              carousel={carousel}
              selectedSlideId={carouselState.selectedSlideId}
              className="mb-4 w-full max-w-2xl"
            />
            <SceneTemplatePreview
              plan={selectedPlan}
              linkedAssets={previewLinkedAssets}
              brandkit={selectedTaskDetails?.brandkit ?? EMPTY_BRANDKIT}
              selectedBlockId={selectedSceneBlockId}
              onSelectBlock={(id) => onSelectSceneBlock(id)}
            />
            <div className="mt-5 flex w-full justify-center">
              <PlannerDecisionDisclosure trace={selectedTaskDetails?.plannerTrace} />
            </div>
          </div>
        ) : planLoadingCopy ? (
          <div
            className="flex max-w-sm flex-col items-center gap-2 text-center"
            role="status"
            aria-live="polite"
          >
            <div className="text-sm font-medium text-foreground">{planLoadingCopy.title}</div>
            <div className="text-xs leading-5 text-muted-foreground">
              {planLoadingCopy.description}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">No plan generated</div>
        )}
      </div>

      {/* Right Sidebar — Block Inspector + Brandkit */}
      {selectedPlan ? (
        <div className="flex w-[400px] shrink-0 flex-col overflow-y-auto border-l bg-background">
          <div className="flex flex-col gap-6 p-5">
            <SceneBlockInspector
              block={selectedSceneBlock}
              linkedAssets={inspectorLinkedAssets}
              imageCandidateGroups={selectedTaskDetails?.imageCandidateGroups ?? []}
              brandkit={selectedTaskDetails?.brandkit ?? EMPTY_BRANDKIT}
              selectedCarouselSlideId={carouselState.selectedSlideId}
            />
            <PublishCopyPanel latestCopy={latestCopy} />
            <CarouselCopyPanel carousel={carousel} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
